import {
  type StorageFileRecordAttributesSubmit,
  type StorageFileRecordSubmit,
} from 'hb-react/shared/hooks/useStorage/types';
import { type useSubscribeToAblyChannel } from 'hb-react/shared/hooks/useSubscribeToAblyChannel';
import { type AblyTokenType } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/types';

import { type StorageStatus, type VirusScanStatus } from './constants';

type SubscribeToAblyChannel = ReturnType<
  typeof useSubscribeToAblyChannel
>['subscribe'];

export interface GlobalStorageFileConfig {
  /** When TRUE, files that throw an error during their processing are automatically marked as removed */
  markFileAsRemovedOnError?: boolean;
  /** In bytes to match File.size */
  maxFileSize?: number;
}

export interface UseGlobalStorageFilesProps {
  config?: GlobalStorageFileConfig;
  initialFiles?: Array<GlobalStorageFile>;
}

export type UseGlobalStorageFiles = (props: UseGlobalStorageFilesProps) => {
  addFiles: AddFiles;
  files: Array<GlobalStorageFile>;
  removeFiles: RemoveFiles;
  setFiles: React.Dispatch<React.SetStateAction<Array<GlobalStorageFile>>>;
};

export interface UseGlobalStorageFileProps {
  config?: GlobalStorageFileConfig;
  initialFile?: MaybeGlobalStorageFile;
  onError?: (file: GlobalStorageFileWithError) => void;
  onSuccess?: (file: MaybeGlobalStorageFile) => void;
}

export type UseGlobalStorageFile = (props?: UseGlobalStorageFileProps) => {
  file?: MaybeGlobalStorageFile;
  isProcessing: boolean;
  removeFile: (file: GlobalStorageFile) => void;
  updateFile: UpdateFile;
};

// Need to consolidate the backend to use only app/serializers/storage_attachment_serializer.rb
// instead of web/uploads/storage_files or engines/storage/lib/dto/storage/polymorphic_attachment
export interface StorageAttachmentRecord {
  byteSize: number;
  compressedUrl: string;
  defaultUrl: string;
  downloadUrl: string;
  /**
   * @deprecated Use filename instead
   */
  fileName: string;
  /**
   * @deprecated Use preview_url instead
   */
  fileUrl: string;
  filename: string;
  friendUrl: string;
  htmlUrl: string;
  iconUrl: string;
  id: number;
  mediumUrl: string;
  mimeType: string;
  miniUrl: string;
  name: string;
  originalUrl: string;
  previewUrl: string;
  /**
   * @deprecated Use byte_size instead
   */
  size: number;
  thumbnailUrl: string;
  virusScanStatus: VirusScanStatus;
  wideUrl: string;
}

export interface StorageAttachmentRecordSnake {
  byte_size: number;
  compressed_url: string;
  default_url: string;
  download_url: string;
  /**
   * @deprecated Use filename instead
   */
  file_name: string;
  /**
   * @deprecated Use preview_url instead
   */
  file_url: string;
  filename: string;
  friend_url: string;
  html_url: string;
  icon_url: string;
  id: number;
  medium_url: string;
  mime_type: string;
  mini_url: string;
  name: string;
  original_url: string;
  preview_url: string;
  /**
   * @deprecated Use byte_size instead
   */
  size: number;
  thumbnail_url: string;
  virus_scan_status: VirusScanStatus;
  wide_url: string;
}

/**
 * @deprecated Use StorageAttachmentRecord instead.
 *
 * Need to consolidate the backend to use only app/serializers/storage_attachment_serializer.rb instead
 * of web/uploads/storage_files or engines/storage/lib/dto/storage/polymorphic_attachment
 */
export interface StorageFileRecord {
  default_url: string;
  file_name: string; // 'http://aeneas.localhost:3000/storage/593/download'
  file_url: string;
  id: number;
  // Same as name. Potential cleanup opportunity
  mime_type: string;
  // 'Same as default_url. Potential cleanup opportunity
  name: string; // 'image/png'
  size: number;
  virus_scan_status: VirusScanStatus;
}

/**
 * @deprecated Use StorageAttachmentRecord instead.
 *
 * Need to consolidate the backend to use only app/serializers/storage_attachment_serializer.rb instead
 * of web/uploads/storage_files or engines/storage/lib/dto/storage/polymorphic_attachment
 */
export interface StorageFileRecordFromPolyAttachment {
  download_url: string;
  id: number; // From polymorphic_attachment
  metadata: {
    filename: string;
    mime_type: string;
    scanned: VirusScanStatus;
    // From polymorphic_attachment
    size: number; // 'image/png'
  };
}

export type StorageRecordTypes =
  | StorageAttachmentRecord
  | StorageFileRecord
  | StorageFileRecordFromPolyAttachment;

export interface StorageAttachmentAttributes {
  _destroy: boolean;
  id: number;
}

export interface FileMetadata {
  name: string;
  type: string;
  url: string;
}

export interface GlobalStorageFile {
  abortController: AbortController;
  /** Used for image preview in hb-react/shared/components/DesignSystem/QuillEditor/Images/index.jsx */
  download_url?: string;
  error?: string | null;
  /** New Files only. */
  file?: File;
  /** Existing Files only. */
  fileMetadata?: FileMetadata;
  /** Existing Files only. Newly uploaded/attached files will have a shrineKey instead */
  id?: number;
  isRemoved?: boolean;
  /** Unique temporary uuid used to track the file in the frontend. */
  key: string;
  /** Tracks the upload progress of the file synchronously with uploadPercentage */
  onProgress?: ProgressHandler;
  /** New files only. Existing files will have an id instead */
  shrineKey?: string;
  storageStatus?: StorageStatus;
  /** Tracks the upload progress of the file synchronously with onProgress */
  uploadPercentage?: number;
  virusScanStatus?: VirusScanStatus;
  virusScanTimeout?: NodeJS.Timeout;
}

export interface GlobalStorageFileWithError extends GlobalStorageFile {
  error: string;
}

export interface UpdateFileProps {
  file?: File | null;
  key?: string;
}

interface RemoveFilesProps {
  files: Array<GlobalStorageFile>;
}

export type GlobalStorageFileIdentifiers = Partial<
  Pick<GlobalStorageFile, 'id' | 'key' | 'shrineKey'>
>;

export type UpdateStoredFileType = (
  identifiers: GlobalStorageFileIdentifiers,
  fileOrFunc:
    | Partial<GlobalStorageFile>
    | ((prevFile: GlobalStorageFile) => GlobalStorageFile),
) => void;

export interface VirusScanningMessage {
  data?: {
    key: string;
    scan_status: VirusScanStatus;
  };
}

export type GetAblyVirusScanMessageHandler = (
  updateStoredFile: UpdateStoredFileType,
  markFileAsRemovedOnError?: boolean,
) => (message: VirusScanningMessage) => void;

export type ProgressHandler = (percentage: number) => void;

export interface FileData {
  file: File;
  key?: string;
  onProgress?: ProgressHandler;
}

export interface PresignAndUploadProps {
  controller: AbortController;
  file: File;
  inBO?: boolean;
  key: string;
  networkId?: number | string;
  onProgress?: ProgressHandler;
  updateStoredFile: UpdateStoredFileType;
  virusScanningEnabled: boolean;
}

export type UpdateStoredFile = (
  identifiers: GlobalStorageFileIdentifiers,
  fileOrFunc:
    | Partial<GlobalStorageFile>
    | ((prevFile: GlobalStorageFile) => GlobalStorageFile),
) => void;

export type AddFiles = (
  filesData: Array<FileData>,
) => Promise<Array<PromiseSettledResult<string | null>>>;

export type RemoveFiles = (props: RemoveFilesProps) => void;

export type UpdateFile = (props: UpdateFileProps) => Promise<string | null>;

interface GetUpdateStoredFilesProps {
  setStoredFiles: React.Dispatch<
    React.SetStateAction<Array<GlobalStorageFile>>
  >;
}

export type GetUpdateStoredFiles = (
  props: GetUpdateStoredFilesProps,
) => UpdateStoredFile;

export enum OwnerType {
  ADMIN = 'admin',
  USER = 'user',
}

interface GetAddFilesProps {
  currentNetworkId?: number | string;
  markFileAsRemovedOnError?: boolean;
  maxFileSize?: number;
  ownerType?: OwnerType;
  setStoredFiles: React.Dispatch<
    React.SetStateAction<Array<GlobalStorageFile>>
  >;
  subscribeToAblyChannel: SubscribeToAblyChannel;
  updateStoredFile: UpdateStoredFile;
  virusScanningEnabled: boolean;
}

export type GetAddFiles = (props: GetAddFilesProps) => AddFiles;

interface GetRemoveFilesProps {
  setStoredFiles: React.Dispatch<
    React.SetStateAction<Array<GlobalStorageFile>>
  >;
}

export type GetRemoveFiles = (props: GetRemoveFilesProps) => RemoveFiles;

export type MaybeGlobalStorageFile = GlobalStorageFile | null | undefined;

interface GetUpdateStoredFileProps {
  setStoredFile: React.Dispatch<React.SetStateAction<MaybeGlobalStorageFile>>;
}

export type GetUpdateStoredFile = (
  props: GetUpdateStoredFileProps,
) => UpdateStoredFile;

interface GetUpdateFileProps {
  abortController?: AbortController;
  config?: GlobalStorageFileConfig;
  currentNetworkId?: number | string;
  ownerType?: OwnerType;
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  setStoredFile: React.Dispatch<React.SetStateAction<MaybeGlobalStorageFile>>;
  subscribeToAblyChannel: SubscribeToAblyChannel;
  updateStoredFile: UpdateStoredFile;
  virusScanningEnabled: boolean;
}

export type GetUpdateFile = (props: GetUpdateFileProps) => UpdateFile;

export type GetStorageAttributesFieldName = (fieldName: string) => string;

export type FindImagesInContent = (
  content?: string | null,
) => Array<GlobalStorageFile>;

export type GetMagicKeyUrl = (props: {
  fieldName: string;
  shrineId: string;
}) => string;

export type GetAblyVirusScanChannel = (
  ownerType: OwnerType | undefined,
  ownerId: number | undefined,
) => string;

export type GetAblyTokenType = (ownerType: OwnerType) => AblyTokenType;

export type FileMatchesIdentifiers = (
  file: GlobalStorageFile,
  identifiers: GlobalStorageFileIdentifiers,
) => boolean;

export type IsInLoadingState = (
  file: GlobalStorageFile,
  virusScanningEnabled: boolean,
) => boolean;

export type AreFilesReadyToSubmit = (
  files: Array<GlobalStorageFile>,
  virusScanningEnabled: boolean,
) => boolean;

export type FormatAddedFileForSubmit = (
  gsFile?: MaybeGlobalStorageFile,
) => StorageFileRecordSubmit | null | undefined;

export type FormatAddedFilesForSubmit = (
  files: Array<GlobalStorageFile>,
) => Array<StorageFileRecordSubmit>;

export type FormatRemovedFilesForSubmit = (
  files: Array<GlobalStorageFile>,
) => Array<StorageFileRecordAttributesSubmit>;

interface GetInitialFilesProps {
  contentHtml?: string;
  files?: Array<StorageRecordTypes>;
}

export type GetInitialFiles = (
  props: GetInitialFilesProps,
) => Array<GlobalStorageFile>;

interface AttachFileProps {
  attachableId: number | string;
  attachableType: string;
  fieldName: string;
  file: StorageFileRecordSubmit;
}

export type AttachFile = (props: AttachFileProps) => Promise<any>;

export interface UseGlobalStorageFileParams {
  config?: any;
  initialFile?: MaybeGlobalStorageFile;
  onError?: (file: GlobalStorageFileWithError) => void;
  /**
   * Invoked when the current GlobalStorageFile has been successfully updated or removed.
   *
   * @param file The newly added GlobalStorageFile object. If the existing file was removed, this will be null.
   * @returns void
   */
  onSuccess?: (file: GlobalStorageFile | null) => void;
}

export interface UseGlobalStorageFileReturnType {
  file: MaybeGlobalStorageFile;
  isProcessing: boolean;
  updateFile: (props: UpdateFileProps) => Promise<string | null>;
}
